package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import kotlin.math.abs

/** Une métadonnée de ligne : une icône facultative et un texte court. */
data class TeddMeta(val text: String, val icon: String? = null, val color: Color? = null)

/**
 * Ligne de tâche : la case, le titre, puis ses métadonnées (échéance, session,
 * récurrence, projet). Une tâche faite est barrée et s'efface ; une échéance dépassée
 * est en rouge **et** précédée de l'icône d'alerte : la couleur seule ne dit rien.
 *
 * [priorityColor] pose un trait à gauche de la ligne pour une priorité haute ou moyenne.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun TeddTaskItem(
    title: String,
    done: Boolean,
    onToggle: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    due: String? = null,
    overdue: Boolean = false,
    meta: List<TeddMeta> = emptyList(),
    priorityColor: Color? = null,
    round: Boolean = false,
    onClick: (() -> Unit)? = null,
    trailing: (@Composable () -> Unit)? = null,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(IntrinsicSize.Min)
            .then(
                if (onClick != null) Modifier.teddClickable(onClick, RoundedCornerShape(TeddTokens.RMd), role = null)
                else Modifier,
            )
            .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        if (priorityColor != null) {
            Box(Modifier.width(3.dp).fillMaxHeight().background(priorityColor, RoundedCornerShape(2.dp)))
        }
        TeddCheckbox(done, onToggle, Modifier.padding(top = 1.dp), round = round)
        Column(Modifier.weight(1f)) {
            Text(
                text = title,
                color = if (done) TeddTokens.InkSubtle else TeddTokens.Ink,
                style = TeddType.Body,
                textDecoration = if (done) TextDecoration.LineThrough else null,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
            if (due != null || meta.isNotEmpty()) {
                FlowRow(
                    modifier = Modifier.padding(top = 2.dp),
                    horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    if (due != null) {
                        MetaItem(
                            TeddMeta(
                                text = due,
                                icon = if (overdue) "alert" else "clock",
                                color = if (overdue) TeddTokens.Danger else null,
                            ),
                            mono = true,
                        )
                    }
                    meta.forEach { MetaItem(it) }
                }
            }
        }
        if (trailing != null) Box(Modifier.align(Alignment.CenterVertically)) { trailing() }
    }
}

@Composable
private fun MetaItem(meta: TeddMeta, mono: Boolean = false) {
    val color = meta.color ?: TeddTokens.InkMuted
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
        if (meta.icon != null) TeddIcon(name = meta.icon, size = 13.dp, color = color)
        Text(meta.text, color = color, style = if (mono) TeddType.MonoSm else TeddType.Caption, maxLines = 1)
    }
}

/** Taille d'un montant. */
enum class TeddAmountSize { Sm, Md, Lg }

/** Sépare les milliers par une espace insécable : `1 245 000`. */
fun formatThousands(value: Long): String =
    abs(value).toString().reversed().chunked(3).joinToString(" ").reversed()

/**
 * Montant : chiffres tabulaires en Geist Mono, milliers séparés par une espace insécable,
 * devise après, plus petite. Un négatif s'écrit avec le signe moins (`−`), jamais entre
 * parenthèses. Avec [signed], un positif est précédé de `+` et passe en couleur de succès.
 */
@Composable
fun TeddAmount(
    value: Long,
    modifier: Modifier = Modifier,
    currency: String? = "FCFA",
    size: TeddAmountSize = TeddAmountSize.Md,
    signed: Boolean = false,
    color: Color? = null,
) {
    val style = when (size) {
        TeddAmountSize.Sm -> TeddType.Mono.copy(fontWeight = androidx.compose.ui.text.font.FontWeight(500))
        TeddAmountSize.Md -> TeddType.Amount
        TeddAmountSize.Lg -> TeddType.AmountLg
    }
    val sign = when {
        value < 0 -> "− "
        signed && value > 0 -> "+ "
        else -> ""
    }
    val ink = color ?: if (signed && value > 0) TeddTokens.Success else TeddTokens.Ink
    val muted = TeddTokens.InkMuted
    Text(
        text = buildAnnotatedString {
            append(sign + formatThousands(value))
            if (currency != null) {
                withStyle(SpanStyle(color = muted, fontSize = 0.62.em, letterSpacing = 0.sp)) {
                    append(" $currency")
                }
            }
        },
        modifier = modifier,
        color = ink,
        style = style,
        maxLines = 1,
    )
}

/**
 * Ligne de transaction : pastille d'icône, libellé, métadonnées (catégorie, compte, date),
 * puis le montant. Une dépense reste à l'encre ; seule une entrée prend la couleur de succès.
 */
@Composable
fun TeddTransactionRow(
    label: String,
    amount: Long,
    modifier: Modifier = Modifier,
    meta: List<String> = emptyList(),
    icon: String = "finance",
    currency: String? = "FCFA",
    recurring: Boolean = false,
    onClick: (() -> Unit)? = null,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .then(
                if (onClick != null) Modifier.teddClickable(onClick, RoundedCornerShape(TeddTokens.RMd), role = null)
                else Modifier,
            )
            .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        Box(
            Modifier.size(36.dp).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = icon, size = 18.dp, color = TeddTokens.InkMuted)
        }
        Column(Modifier.weight(1f)) {
            Text(label, color = TeddTokens.Ink, style = TeddType.Body, maxLines = 1, overflow = TextOverflow.Ellipsis)
            if (meta.isNotEmpty() || recurring) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    if (recurring) TeddIcon(name = "repeat", size = 12.dp, color = TeddTokens.InkMuted)
                    Text(
                        meta.joinToString(" · "),
                        color = TeddTokens.InkMuted,
                        style = TeddType.Caption,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
        TeddAmount(amount, currency = currency, signed = true)
    }
}
